import React, { useState } from 'react';
import { UserPlus, Loader2, AlertCircle, Check } from 'lucide-react';
import { useAuth } from '../../contexts';

const ROLE_OPTIONS = [
  { value: 'viewer', label: 'Observateur', desc: 'Lecture seule : Planning et documents' },
  { value: 'member', label: 'Membre', desc: 'Lecture/écriture sur les chantiers' },
  { value: 'manager', label: 'Chef d\'équipe', desc: 'Créer, modifier et envoyer les devis/factures' },
  { value: 'admin', label: 'Admin', desc: 'Accès total : Paramètres, Banque, Équipe' }
];

const InviteMemberModal = ({ companyId, companyName, onClose, onInviteSent }) => {
  const { user } = useAuth();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSendInvite = async () => {
    const cleanEmail = inviteEmail.trim().toLowerCase();
    if (!cleanEmail) { setError('Veuillez entrer une adresse email'); return; }

    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailRegex.test(cleanEmail)) { setError('Veuillez entrer une adresse email valide'); return; }
    if (cleanEmail.includes('\n') || cleanEmail.includes('\r') || cleanEmail.includes('<') || cleanEmail.includes('>')) { setError('Adresse email invalide'); return; }
    if (cleanEmail.length > 254) { setError('Adresse email trop longue'); return; }

    setIsSending(true);
    setError('');
    try {
      const { createInvitation } = await import('../../lib/invitations');
      await createInvitation({ companyId, companyName, email: cleanEmail, role: inviteRole });
      setSuccess(true);
      setInviteEmail('');
      setInviteRole('member');
      setTimeout(() => { setSuccess(false); onInviteSent(); onClose(); }, 2000);
    } catch (e) {
      console.error('Erreur invitation:', e);
      setError('Erreur lors de l\'envoi de l\'invitation. Vérifiez vos droits d\'accès.');
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all scale-100">
        <div className="bg-gradient-to-r from-green-600 to-emerald-700 p-6 text-white">
          <h3 className="font-bold text-xl flex items-center gap-2">
            <UserPlus size={24} /> Ajouter un membre
          </h3>
          <p className="text-green-100 text-sm mt-1">Invitez quelqu'un à rejoindre {companyName}</p>
        </div>
        <div className="p-6 space-y-4">
          {success ? (
            <div className="flex flex-col items-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Check size={32} className="text-green-600" />
              </div>
              <p className="text-lg font-bold text-slate-800">Invitation envoyée !</p>
              <p className="text-sm text-slate-500 mt-1">{inviteEmail} va recevoir une invitation.</p>
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase mb-2 block">Email du membre</label>
                <input
                  type="email"
                  className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-green-500 transition font-medium text-slate-700"
                  placeholder="jean@example.com"
                  value={inviteEmail}
                  onChange={(e) => { setInviteEmail(e.target.value); setError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendInvite()}
                  autoFocus
                  disabled={isSending}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase mb-3 block">Rôle</label>
                <div className="space-y-2">
                  {ROLE_OPTIONS.map(role => (
                    <button
                      key={role.value}
                      onClick={() => setInviteRole(role.value)}
                      disabled={isSending}
                      className={`w-full text-left p-3 rounded-lg border-2 transition ${inviteRole === role.value ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-green-300 bg-white'} disabled:opacity-50`}
                    >
                      <p className="font-bold text-slate-800">{role.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{role.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
                  <AlertCircle size={16} /> {error}
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={onClose} disabled={isSending} className="px-5 py-2.5 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition disabled:opacity-50">
                  Annuler
                </button>
                <button
                  onClick={handleSendInvite}
                  disabled={!inviteEmail.trim() || isSending}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg transition transform active:scale-95 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSending ? <><Loader2 size={18} className="animate-spin" /> Envoi...</> : <>Envoyer l'invitation</>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteMemberModal;
